import { ArrowDropDown as ArrowDropDownIcon, Cancel as CancelIcon, ClearOutlined } from '@mui/icons-material';
import { Chip, IconButton } from '@mui/material';
import React from 'react';

import { CHIP_COLOR_CLASS } from '@/components/fuel-autocomplete/FuelAutoComplete';
import { useVNFieldHelpContext, VNFieldHelpIconButton } from '@/components/VNFieldHelp';

import type { DisplaySlotProps } from '../types';

/**
 * Default display slot for VNDropdown.
 *
 * Renders a styled button trigger with a floating label, selected value(s), and a caret icon.
 * Satisfies `DisplaySlotProps` so it can be replaced by a custom component.
 */
const VNDropdownDisplay = ({
  selectedOptions,
  isOpen,
  onToggle,
  onClearOption,
  label,
  placeholder = '',
  disabled = false,
  error,
  multiple = false,
  inputRef,
  triggerId,
  listboxId,
  focusedOptionId,
  onKeyDown,
  disableBorder = false,
  compact = false,
  allowClear = false,
  onClearAll,
  hideFloatingLabel = false,
  chipColor = 'default',
}: DisplaySlotProps) => {
  const { iconConfig: fieldHelpIconConfig } = useVNFieldHelpContext();
  const chipColorClass = CHIP_COLOR_CLASS[chipColor];
  const hasValue = selectedOptions.length > 0;
  const isLabelFloating = isOpen || hasValue;
  const errorId = `${triggerId}-error`;
  const showClearButton = allowClear && hasValue && !disabled;

  const renderContent = () => {
    // Compact multi-select: show placeholder + count badge; no chips
    if (compact && multiple) {
      return (
        <div className="vn-dropdown-display__compact-multiselect">
          <span className="vn-dropdown-display__placeholder">{placeholder}</span>
          {selectedOptions.length > 0 && (
            <span className="vn-dropdown-display__count-badge" aria-label={`${selectedOptions.length} selected`}>
              {selectedOptions.length}
            </span>
          )}
        </div>
      );
    }

    if (!hasValue) {
      return (
        <span className="vn-dropdown-display__placeholder">
          {/* Only show placeholder text when the label is floating (or there is no label) */}
          {!label || isLabelFloating ? placeholder : ''}
        </span>
      );
    }

    if (multiple) {
      const first = selectedOptions[0];
      const overflow = selectedOptions.length - 1;
      return (
        <div className="vn-dropdown-display__chips">
          <Chip
            key={first.id}
            label={first.label}
            title={first.label}
            size="small"
            className={chipColorClass}
            onDelete={(e: React.MouseEvent) => {
              e.stopPropagation();
              onClearOption(first.id);
            }}
            deleteIcon={<CancelIcon onMouseDown={(e: React.MouseEvent) => e.stopPropagation()} />}
          />
          {overflow > 0 && <span className="vn-dropdown-display__overflow-badge">+{overflow}</span>}
        </div>
      );
    }

    return <span className="vn-dropdown-display__value">{selectedOptions[0].label}</span>;
  };

  return (
    <div
      className={[
        'vn-dropdown-trigger',
        isLabelFloating && label && !hideFloatingLabel ? 'vn-dropdown-trigger--label-floating' : '',
        error ? 'vn-dropdown-trigger--error' : '',
        disabled ? 'vn-dropdown-trigger--disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label && !hideFloatingLabel && (
        <label
          htmlFor={triggerId}
          className={['vn-dropdown-trigger__label', isLabelFloating ? 'vn-dropdown-trigger__label--floating' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {label}
        </label>
      )}

      <button
        id={triggerId}
        ref={inputRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={focusedOptionId}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? errorId : undefined}
        disabled={disabled}
        className={[
          'vn-dropdown-display',
          isOpen ? 'vn-dropdown-display--open' : '',
          disabled ? 'vn-dropdown-display--disabled' : '',
          error ? 'vn-dropdown-display--error' : '',
          disableBorder || compact ? 'vn-dropdown-display--borderless' : '',
          showClearButton ? 'vn-dropdown-display--with-clear' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={onToggle}
        onKeyDown={onKeyDown}
      >
        <span className="vn-dropdown-display__content">{renderContent()}</span>
        <span className="vn-dropdown-display__trailing">
          <ArrowDropDownIcon
            className={['vn-dropdown-display__caret', isOpen ? 'vn-dropdown-display__caret--open' : ''].filter(Boolean).join(' ')}
            fontSize="small"
            aria-hidden="true"
          />
          {fieldHelpIconConfig && (
            <span
              className="vn-dropdown-trigger__field-help"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <VNFieldHelpIconButton {...fieldHelpIconConfig} />
            </span>
          )}
        </span>
      </button>

      {showClearButton && (
        <IconButton
          size="small"
          className="vn-dropdown-display__clear-btn"
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClearAll?.();
          }}
          aria-label="Clear selection"
          tabIndex={-1}
        >
          <ClearOutlined fontSize="small" />
        </IconButton>
      )}

      {error && (
        <p id={errorId} className="vn-dropdown-trigger__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default React.memo(VNDropdownDisplay);
